import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { viewTransition } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    let variantSlideIndex = -1;
    if (this.dataset.ruvelFeaturedFirst === 'true' && newMediaGallery.dataset.ruvelFeaturedFirst === 'true') {
      const showVariant =
        this.dataset.ruvelShowVariant === 'true' ||
        this.dataset.ruvelVisualOptions !== newMediaGallery.dataset.ruvelVisualOptions;

      newMediaGallery.dataset.ruvelShowVariant = String(showVariant);

      if (showVariant) {
        const mediaId = newMediaGallery.dataset.ruvelVariantMediaId;
        const slides = Array.from(newMediaGallery.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide'));
        variantSlideIndex = slides.findIndex((slide) => slide.querySelector('[data-media-id]')?.getAttribute('data-media-id') === mediaId);
        const grid = newMediaGallery.querySelector('.media-gallery__grid');
        const gridMedia = Array.from(grid?.children ?? []).find(
          (item) => item.querySelector('[data-media-id]')?.getAttribute('data-media-id') === mediaId
        );
        if (gridMedia) grid?.prepend(gridMedia);
      }
    }

    this.replaceWith(newMediaGallery);
    if (variantSlideIndex >= 0) {
      if (viewTransition.current) await viewTransition.current;
      requestAnimationFrame(() => {
        if (newMediaGallery.isConnected) {
          newMediaGallery.slideshow?.select(variantSlideIndex, undefined, { animate: false });
        }
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}


